import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('sponsorToken'));
  const [loading, setLoading] = useState(true);

  const saveToken = (t) => {
    setToken(t);
    if (t) localStorage.setItem('sponsorToken', t);
    else localStorage.removeItem('sponsorToken');
  };

  const fetchProfile = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get('/api/auth/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data.user);
    } catch {
      setUser(null);
      saveToken(null);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const login = async (email, password) => {
    const res = await axios.post('/api/auth/login', { email, password });
    saveToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const signup = async (name, email, password) => {
    const res = await axios.post('/api/auth/signup', { name, email, password });
    return res.data;
  };

  const logout = () => {
    saveToken(null);
    setUser(null);
  };

  const refreshProfile = () => fetchProfile();

  const isSubscribed = () => {
    if (user && user.is_admin) return true;
    if (!user || !user.subscription_end) return false;
    return new Date(user.subscription_end) > new Date();
  };

  const isAdmin = () => {
    return !!(user && user.is_admin);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, logout, refreshProfile, isSubscribed, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
